import type { Role } from "@ub/shared";
import type { StaffRoleDb } from "../api/types";

/**
 * Map DB-side staff.role values to the frontend Role union.
 *
 * `admin_hall` is treated as `OP` per the A7 clarification (2026-05-27):
 * hall-admin is a per-day runtime state of an operator, not a primary role.
 */
const DB_TO_FRONT: Record<StaffRoleDb, Role> = {
  dc: "DC",
  oo: "OO",
  eo: "EO",
  operator: "OP",
  admin_hall: "OP",
};

/** "operator" → "OP", "dc" → "DC", etc. */
export function dbRoleToFront(role: StaffRoleDb): Role {
  return DB_TO_FRONT[role];
}

/** Inverse: "OP" → "operator", "DC" → "dc", … */
export function frontRoleToDb(role: Role): StaffRoleDb {
  switch (role) {
    case "DC":
      return "dc";
    case "OO":
      return "oo";
    case "EO":
      return "eo";
    case "OP":
      return "operator";
  }
}

/** URL-safe role slug used in routes — lowercase of the front-role token. */
export function roleSlug(role: Role): string {
  return role.toLowerCase();
}

/** Parse a URL slug back to a Role. Returns null for unknowns. */
export function parseRoleSlug(slug: string): Role | null {
  switch (slug.toLowerCase()) {
    case "dc":
      return "DC";
    case "oo":
      return "OO";
    case "eo":
      return "EO";
    case "op":
      return "OP";
    default:
      return null;
  }
}

/**
 * Long display name — used on role-cards (B.1.3 RoleSwitcher) and any other
 * place where full Russian wording is needed. Mixed RU/EN per DECISIONS A7.
 */
export function roleLabel(role: Role): string {
  switch (role) {
    case "DC":
      return "ДЕПАРТАМЕНТ КОММЕРЦИИ";
    case "OO":
      return "OFFICE OPERATIONS";
    case "EO":
      return "EXECUTIVE OFFICE";
    case "OP":
      return "ОПЕРАТОР";
  }
}

/**
 * Short Latin-only label used inside the HUD role-badge (B.2.8). Orbitron
 * — the display font — has no Cyrillic glyphs, so Russian text on the HUD
 * silently falls back to Chakra Petch and looks off. The badge expects
 * `LABEL | {nickname}` format with a pipe-separator inside the plaque.
 */
export function roleHudLabel(role: Role): string {
  switch (role) {
    case "DC":
      return "DEP. OF COMMERCE";
    case "OO":
      return "OFFICE OPERATIONS";
    case "EO":
      return "EXECUTIVE OFFICE";
    case "OP":
      return "OPERATOR";
  }
}

/** Single-line tagline shown under a role on the role-switcher card. */
export function roleTagline(role: Role): string {
  switch (role) {
    case "DC":
      return "Бронирования, лиды, клиенты";
    case "OO":
      return "Расстановка операторов, выплаты";
    case "EO":
      return "Управление сетью, финансы";
    case "OP":
      return "Свои игры, график, отчёты";
  }
}
